import { SuggestionPlugin } from '@alpod-ai-writer/plate-plugins/suggestion/react';
import type { Editor, Value } from '@udecode/plate';
import { Plate, PlateContent, type PlateContentProps,type PlateEditor,useEditorContainerRef, useEditorRef } from '@udecode/plate/react';
import clsx from 'clsx';
import React, { type LegacyRef,useCallback, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { detectOS } from '../../../lib/responsivity.utils';
import { InlineToolbar } from './inlineToolbar';

export const InlineEditor: React.FC<{
    editor: PlateEditor;
    isSuggesting?: boolean;
    focus?: boolean;
    readOnly?: boolean;
    className?: string;
    toolbarClass?: string;
    containerClass?: string;
    onValueChange?: (value: Value) => void;
}> = ({
    editor,
    isSuggesting,
    focus,
    readOnly,
    className,
    toolbarClass,
    containerClass,
    onValueChange
}) => {
    const handleValueChange = useCallback((options: {
        editor: Editor;
        value: Value;
    }) => {
        onValueChange?.(options.value);
    }, [ onValueChange ]);

    useEffect(() => {
        if (focus && editor) {
            const t = setTimeout(() => {
                editor.tf.select(editor, {
                    focus: true,
                    edge: 'end',
                });
            });

            return () => {
                clearTimeout(t);
            };
        }
    }, [ editor, focus ]);

    useEffect(() => {
        if (editor) {
            editor.setOption(SuggestionPlugin, 'isSuggesting', !!isSuggesting);
        }
    }, [ editor, isSuggesting ]);

    return (
        <Plate
            editor={editor}
            readOnly={readOnly}
            onValueChange={handleValueChange}
        >
            <div className={className}>
                <div className={clsx(
                    'flex flex-row items-center justify-start md:justify-center',
                    toolbarClass
                )}>
                    <InlineToolbar />
                </div>
                <InlineEditorContainer className={clsx(
                    '[&_.slate-selection-area]:border [&_.slate-selection-area]:border-primary [&_.slate-selection-area]:bg-primary/10',
                    containerClass
                )}>
                    <InlineEditorInner placeholder={
                        {
                            'Windows': 'Začněte psát nebo stiskněte Win+J pro použít AI...',
                            'Macintosh': 'Začněte psát nebo stiskněte Cmd+J pro použít AI...',
                            'Linux': 'Začněte psát nebo stiskněte Meta+J pro použít AI...',
                            'Other': 'Začněte psát...',
                        }[detectOS()]
                    } className='outline-none' />
                </InlineEditorContainer>
            </div>
        </Plate>
    );
};

export const InlineEditorContainer: React.FC<React.HTMLAttributes<HTMLDivElement>> = (props) => {
    const editor = useEditorRef();
    const containerRef = useEditorContainerRef();
    return (
        <div id={editor.uid} ref={containerRef as LegacyRef<HTMLDivElement>} {...props} />
    );
};

export const InlineEditorInner = React.forwardRef<HTMLDivElement, PlateContentProps>(
    ({ ...props }, ref) => {
        return (
            <DndProvider backend={HTML5Backend}>
                <PlateContent
                    ref={ref}
                    {...props}
                />
            </DndProvider>
        );
    }
);
